/* eslint-disable  @typescript-eslint/no-explicit-any */
import { CDX_ACTIONS, FormActionType } from 'types/form';
import { ISaveloanInfoPayload } from '@components/SoftPullAcknowledgementPage/SoftPullAcknowledgementPage';
import { Phase } from '@shared/hooks/useScreenNavigator';
import { getAccurateEstimatedOffer, getCreditSummary, ISoftpullRequestPayload } from './getAccurateEstimatedOffer';
import { postSelectedLoan } from './lenderInformation';
import { createErrorResponse } from './utils';

interface IHandleSoftPullProps {
  softpullRequestPayload: ISoftpullRequestPayload;
  balanceAmountPayload: ISaveloanInfoPayload;
  currentStep: string;
  next: (currentStep: string, phase: Phase, data: Record<string, unknown>, shouldUpdateProgress?: boolean) => void;
  dispatch: React.Dispatch<FormActionType>;
  trackFormSubmit: (eventType: string, parameters?: any) => void;
}

type IHandleSoftPullPropsOmit = Omit<IHandleSoftPullProps, 'balanceAmountPayload'>;
interface Loan {
  lender: string;
  currentBalance: number;
  openDate: string;
  payment: number;
  remainingTerm: number;
}

export const handleSoftPullLogic = async ({
  softpullRequestPayload,
  balanceAmountPayload,
  currentStep,
  next,
  dispatch,
  trackFormSubmit,
}: IHandleSoftPullProps) => {
  const creditSummaryResponse = await getCreditSummary(softpullRequestPayload);

  if ('error' in creditSummaryResponse) {
    dispatch({
      type: CDX_ACTIONS.SUBMIT_API_ERROR,
      payload: creditSummaryResponse,
    });
  } else {
    const loans = creditSummaryResponse?.loanDetails;
    //If loans are empty then navigate to manual lender info page
    if (loans.length === 0) {
      trackFormSubmit('Loan details not found from credit summary, navigated to manual lender info screen');
      dispatch({
        type: CDX_ACTIONS.SET_LOADING,
        payload: false,
      });
      next(currentStep, 'postSubmitOutcome', { loansCount: loans.length}, true);
    } else {
      // if loans are present then find delta of currentMonthlyPayment if it is less than equal to 50$
      const currentMonthlyPayment = balanceAmountPayload?.currentMonthlyPayment;
      const tolerance = 50;
      const findSimilarPaymentLoans = (loans: Loan[], targetPayment: number, tolerance: number): Loan[] => {
        return loans.filter((loan) => {
          const difference = Math.abs(loan.payment - targetPayment);
          return difference <= tolerance;
        });
      };
      const matchingLoans = findSimilarPaymentLoans(loans, currentMonthlyPayment, tolerance);

      // Only single match then update using loan details api
      if (matchingLoans.length === 1) {
        const matchedLoan = matchingLoans[0];
        const selectedLoanDetails = {
          leadId: balanceAmountPayload?.leadId,
          lender: matchedLoan?.lender ?? '',
          currentBalance: matchedLoan?.currentBalance ?? 0,
          payment: matchedLoan?.payment ?? 0,
          remainingTerm: matchedLoan?.remainingTerm ?? 0,
        };
        const submitResponse = await postSelectedLoan(
          {
            contactId: balanceAmountPayload?.contactId,
            assetId: balanceAmountPayload?.assetId,
          },
          selectedLoanDetails
        );

        trackFormSubmit('Form Submitted', {
          submission_status: true,
          soft_pull_acknowledged: true,
          current_estimated_monthly_payment: matchedLoan?.payment ?? 0,
          current_estimated_loan_balance: matchedLoan?.currentBalance ?? 0,
          forward_screen_name: 'offers',
        });

        trackFormSubmit('Loan Information Provided', {
          form_context: 'loan_details',
          lead_id: balanceAmountPayload?.leadId,
          current_loan_payment: matchedLoan?.payment ?? 0,
          current_loan_balance: matchedLoan?.currentBalance ?? 0,
          current_loan_term: matchedLoan?.remainingTerm ?? 0,
          qualified_status: true,
        });

        if (!submitResponse) {
          trackFormSubmit('No asset id found for softpull navigated to first screen');
          localStorage.clear();
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: createErrorResponse(400, 'Error while softpull, please try again'),
          });
          return;
        }
        next(currentStep, 'postSubmitOutcome', { submitResponse}, true);
        if ('error' in submitResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: submitResponse,
          });
          trackFormSubmit('Form Submitted', {
            submission_status: false,
            soft_pull_acknowledged: false,
            submission_fail_reason: submitResponse?.error?.status === 500 ? 'service_unavailable' : submitResponse?.error?.message,
            current_estimated_monthly_payment: null,
            current_estimated_loan_balance: null,
          });
        } else {
          const softPullResponse = await getAccurateEstimatedOffer(softpullRequestPayload);
          if ('error' in softPullResponse) {
            dispatch({
              type: CDX_ACTIONS.SUBMIT_API_ERROR,
              payload: softPullResponse,
            });
            trackFormSubmit('Loan Information Provided', {
              form_context: 'loan_details',
              lead_id: balanceAmountPayload?.leadId,
              current_loan_payment: matchedLoan?.payment ?? 0,
              current_loan_balance: matchedLoan?.currentBalance ?? 0,
              current_loan_term: matchedLoan?.remainingTerm ?? 0,
              qualified_status: false,
              qualified_fail_reason:softPullResponse?.error?.status === 500 ? 'service_unavailable' : softPullResponse?.error?.message?? 'softpull_failed',
            });
          } else {
            if (softPullResponse?.offers.length === 0 && !softPullResponse?.usedDefaultScore) {
              trackFormSubmit('Form Submitted', {
                submission_status: false,
                soft_pull_acknowledged: false,
                submission_fail_reason: 'income_too_low',
                current_estimated_monthly_payment: null,
                current_estimated_loan_balance: null,
              });
            } else {
              dispatch({ type: 'SUBMIT_SOFTPULL_SUCCESS', payload: softPullResponse.offers ?? null });
              trackFormSubmit('Single Loan matched from credit summary, navigated to offer selection screen');
              trackFormSubmit('Consent Update Provided', {
                form_context: 'loan_details',
                consent_type: 'add',
                consent_category: 'credit',
                soft_pull_authorization_consent: true,
                submission_status: true,
              });
              return softPullResponse.offers;
            }
          }
          next(currentStep, 'postSubmitOutcome', { softPullResponse }, true);
        }
      } else {
        trackFormSubmit('Multiple Loan matched from credit summary, navigated to Loan selection screen');
        //Loans count is greater than 1 then navigate to Loan selection screen
        next(currentStep, 'postSubmitOutcome', { matchingLoansCount: matchingLoans.length}, true);
      }

      return null;
    }
  }
};

export const handleFetchOffers = async ({ softpullRequestPayload, currentStep, next, dispatch }: IHandleSoftPullPropsOmit) => {
  const creditSummaryResponse = await getCreditSummary(softpullRequestPayload);
  if ('error' in creditSummaryResponse) {
    dispatch({
      type: CDX_ACTIONS.SUBMIT_API_ERROR,
      payload: creditSummaryResponse,
    });
  } else {
    const softPullResponse = await getAccurateEstimatedOffer(softpullRequestPayload);
    if ('error' in softPullResponse) {
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: softPullResponse,
      });
    } else {
      if (softPullResponse?.offers.length !== 0) {
        dispatch({ type: 'SUBMIT_SOFTPULL_SUCCESS', payload: softPullResponse.offers ?? null });
        return softPullResponse.offers;
      }
      next(currentStep, 'postSubmitOutcome', { softPullResponse }, true);
    }
    return null;
  }
};
